import * as React from "react"
import { NavLink } from "react-router-dom"

import { cn } from "@/lib/utils"

/**
 * LAS PESTAÑAS DE SECCIÓN — la pieza que dice «esto son secciones».
 *
 * ---------------------------------------------------------------------------
 * QUÉ ESTABA MAL
 *
 * Cesar, probando Finanzas: «P&L, flujo de caja, revenue build-up… sólo los
 * encuentras si buscas bien, debería estar diseñado para que sea claro». Y lo
 * mismo, con las mismas palabras, en Contabilidad.
 *
 * No era un problema de dos pantallas: era **el mismo bloque de markup copiado
 * en seis** —Finanzas, Contabilidad, Libros, Estados, Maestros y Holding—, un
 * filete de 2 px bajo un texto gris de 12 px. Sobre folio, con `muted-foreground`
 * al 4.76:1, una fila así se lee como un rastro de migas, no como el índice de
 * la sección. Y en Finanzas son NUEVE entradas: nueve textos grises seguidos son
 * una línea de ruido.
 *
 * ---------------------------------------------------------------------------
 * QUÉ DICE EL BRANDBOOK, Y POR QUÉ RESUELVE ESTO
 *
 * Lámina 20, NAVEGACIÓN: **«Pestaña activa en folio.»** No un subrayado: la
 * pestaña ES la hoja. Es la misma regla que ya rige el riel —el módulo activo va
 * en folio pleno— y la misma metáfora del libro mayor: una pestaña de carpeta
 * que se continúa en la hoja que hay debajo.
 *
 * Así que la banda va hundida (`surface-sunken`, la canaleta) y la pestaña
 * activa va en `surface` —la hoja—, **sin borde inferior**, de modo que se funde
 * con el panel de contenido. Eso es lo que convierte nueve textos en una fila de
 * pestañas: el activo no está marcado, está FUERA de la banda.
 *
 * El filete superior de 2 px es verde libro. **No rojo**, y no es un descuido:
 * el rojo margen «señala, no tapiza: un acento por pieza», y la pieza ya gastó
 * el suyo en la línea de margen del riel y en el punto del titular. Un tercer
 * rojo que además salta de pestaña en pestaña dejaría a los tres sin significar
 * nada. Es exactamente la corrección que se le hizo al riel.
 *
 * ---------------------------------------------------------------------------
 * EL HOVER HACE DOS COSAS
 *
 * Lámina 16: «el salto entre estados debe verse sin buscarlo». Fondo de hoja al
 * 60% y el texto a tinta plena. Una sola señal —sólo color de texto, que es lo
 * que había— no se ve sobre una banda hundida, y eso significa no saber que son
 * pulsables hasta pulsar una.
 */

export interface SeccionTab {
  /** Ruta relativa, como la espera `NavLink`. */
  to: string
  label: string
  /** `true` para la pestaña que representa la propia sección (índice). */
  end?: boolean
}

interface Props {
  tabs: SeccionTab[]
  /** Qué familia de pestañas es. Obligatorio: es lo que oye un lector de pantalla. */
  ariaLabel: string
  className?: string
  /** Un mando al extremo derecho de la banda: el «Comentar esta vista». */
  derecha?: React.ReactNode
}

export function SeccionTabs({ tabs, ariaLabel, className, derecha }: Props) {
  return (
    <nav
      aria-label={ariaLabel}
      data-guia="pestanas"
      className={cn(
        "flex flex-wrap items-end gap-0.5 rounded-t-panel border-b bg-surface-sunken/70 px-1 pt-1",
        className,
      )}
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          // La guía ilumina una pestaña concreta por su ruta (ver `guia/pasos.ts`).
          data-guia={`pestana-${tab.to}`}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              "-mb-px rounded-t-md border border-b-0 px-2.5 py-1.5 text-xs font-medium",
              "transition-colors duration-100",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isActive
                ? // La hoja: se funde con el panel de abajo. Ver el docstring.
                  "border-border border-t-2 border-t-brand bg-surface text-foreground shadow-xs"
                : "border-transparent text-muted-foreground hover:bg-surface/70 hover:text-foreground",
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
      {derecha ? <div className="ml-auto self-center pb-1 pr-1">{derecha}</div> : null}
    </nav>
  )
}
